/**
 * Fatal error. Thrown only when processing cannot continue.
 *
 * - `INVALID_FILE_FORMAT`: input is not readable as `.xlsx` (corrupt / wrong format / empty)
 * - `MISSING_REQUIRED_SHEET`: both `サービスリスト` and `画面リスト` are missing
 * - `INPUT_TOO_LARGE`: input exceeds size limits (file size / sheet count / row count / cell length)
 * - `DUPLICATE_NAME_AT_SAME_LEVEL`: two or more siblings share the same name (trim-normalized) under
 *   the same parent; name-based matching by downstream logic (e.g. match-existing-targets) would be
 *   ambiguous, so import is rejected
 * - `COMPONENT_REF_INVALID_PLACEMENT`: a component-ref row has 親項目 pointing to an item-type target;
 *   component-ref nodes may only be placed directly under View / Component or under section / overlay
 * - `AMBIGUOUS_PARENT_ITEM_REF`: a 親項目 reference matches more than one item sharing the same name in
 *   the same sheet; name-based parent resolution would be ambiguous (last-wins) and could place the
 *   child under the wrong parent, so import is rejected (rename so parent references are unique)
 *
 * Non-fatal issues are collected as `SpecWarning` in `result.warnings`.
 *
 * Messages are in English (consumers should use `code` + context for localization).
 */
export class SpecError extends Error {
  /** Warnings accumulated up to the time the error was thrown. */
  public warnings?: SpecWarning[];

  constructor(
    public readonly code: SpecErrorCode,
    message: string,
    /** lib 利用側が i18n / UI 表示する際に message 文字列を parse せず使える構造化情報。 */
    public readonly context?: Record<string, unknown>,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "SpecError";
  }
}

/**
 * 全 fatal error コードのランタイム配列。`SpecErrorCode` union はこの配列から派生させ、
 * i18n マッピングの網羅性検証などで enumerate できる (`ALL_WARNING_CODES` と対称)。
 * 各コードの意味は本ファイル冒頭の `SpecError` JSDoc を参照。
 */
export const ALL_ERROR_CODES = [
  "INVALID_FILE_FORMAT",
  "MISSING_REQUIRED_SHEET",
  "INPUT_TOO_LARGE",
  "DUPLICATE_NAME_AT_SAME_LEVEL",
  "COMPONENT_REF_INVALID_PLACEMENT",
  "AMBIGUOUS_PARENT_ITEM_REF",
] as const;

export type SpecErrorCode = (typeof ALL_ERROR_CODES)[number];

/**
 * 警告。処理は継続し、結果の `warnings` 配列に蓄積される。
 *
 * `location` は必須 (どのシート・どの行で起きたかを必ず示す)。
 * `columnName` は列単位で特定できる場合のみ付与。
 */
export type SpecWarning = {
  code: SpecWarningCode;
  message: string;
  location: SpecWarningLocation;
  context?: Record<string, unknown>;
};

export type SpecWarningLocation = {
  sheetName: string;
  /** 1-origin の行番号 (Excel 表示と同じ)。シート全体に関わる警告では省略。 */
  rowIndex?: number;
  columnName?: string;
};

/**
 * 全警告コードのランタイム配列。`SpecWarningCode` union はこの配列から派生させ、
 * テスト fixture の網羅性検証や i18n マッピングの enumerate 用途で再利用できる。
 * 新しい警告コードを追加する場合は本配列に追加すれば union 側にも自動反映される。
 *
 * 各コードの意味:
 * - `duplicate_spec_id`: 一覧シート (サービス/画面/共通コンポーネント) で specId が重複 (先頭行のみ採用、後続行はスキップ)
 * - `missing_spec_id`: 一覧シートで specId 列が空欄 (該当 Service / View / Component をスキップ)
 * - `duplicate_item_name_in_sheet`: 同一詳細シート内で `項目名` が重複 (parentItemRef での名前参照は最後に出現した行に解決される)
 * - `duplicate_item_no_in_sheet`: 詳細シート内で `項目定義No.` が重複 (先勝ち、最初の行のみ採用)
 * - `duplicate_detail_sheet_for_context`: 複数の詳細シートが同じ `画面ID` / `共通コンポーネントID` を宣言 (先勝ち、後続シートはスキップ)
 * - `unknown_service`: 画面リスト / 共通コンポーネントリストの `サービスID` がサービスリストに無い (該当 View / Component をスキップ)
 * - `unknown_screen_id`: `画面_*` シートの `画面ID` が画面リストに無い
 * - `unknown_common_component_id`: `共通_*` シートの `共通コンポーネントID` が共通コンポーネントリストに無い
 * - `unknown_object_type`: 凡例に無い `オブジェクト種別` (Item として継続)
 * - `unknown_transition_type`: 凡例外の `遷移タイプ` (same_tab フォールバック)
 * - `unresolved_item_ref`: データバリデーション / 画面遷移 / 振る舞い / メッセージ が
 *   存在しない `項目定義No.` を参照
 * - `unresolved_parent_item_ref`: item の `親項目` が同シート内の項目名に解決できない (View / Component 直下扱い)
 * - `unresolved_screen_ref`: 画面遷移セクションの `画面ID` (遷移先) が画面リストに無い
 * - `unresolved_component_ref`: 項目定義セクションの `参照コンポーネント` 列で指定した共通コンポーネントIDが共通コンポーネントリストに無い
 * - `invalid_parent_ref`: section / overlay 行に `親項目` が指定されている (View / Component 直下固定のため無視)
 * - `invalid_field_on_non_item_type`: section / overlay 行に item 専用フィールド (画面遷移 等) が指定されている (UI 未対応のため無視)
 * - `circular_parent_ref`: 親項目参照が循環している (例: A.parent=B, B.parent=A)
 */
export const ALL_WARNING_CODES = [
  "duplicate_spec_id",
  "missing_spec_id",
  "duplicate_item_name_in_sheet",
  "duplicate_item_no_in_sheet",
  "duplicate_detail_sheet_for_context",
  "unknown_service",
  "unknown_screen_id",
  "unknown_common_component_id",
  "unknown_object_type",
  "unknown_transition_type",
  "unresolved_item_ref",
  "unresolved_parent_item_ref",
  "unresolved_screen_ref",
  "unresolved_component_ref",
  "invalid_parent_ref",
  "invalid_field_on_non_item_type",
  "circular_parent_ref",
] as const;

export type SpecWarningCode = (typeof ALL_WARNING_CODES)[number];
